import { toast } from "@/hooks/use-toast";
import { type BackupResult } from "@/utils/wordpressApi";
import { getBackups, saveBackups, type BackupRecord } from "./databaseService";
import { backupWebsiteViaPlugin, getPluginRegistration } from "./wordpressPluginService";
import { startWordPressBackup } from "@/utils/wordpressApi";
import { getWebsiteCredentials } from "./wordpressService";
import { scanBackupForThreats } from "./securityService";
import { saveOrUpdateBackupInSupabase, deleteBackupFromSupabase } from "./supabaseBackupService";

/**
 * Creates a new backup for a website, defaulting to incremental backup
 */
export const createBackup = async (websiteId: string): Promise<BackupResult | null> => {
  try {
    console.log(`Creating incremental backup for website ${websiteId}`);
    
    // Check if this website is connected via plugin
    const pluginRegistration = getPluginRegistration(websiteId);
    
    if (pluginRegistration) {
      // Use plugin-based backup with incremental type by default
      console.log(`Using plugin-based incremental backup for website ${websiteId}`);
      
      const result = await backupWebsiteViaPlugin(websiteId, 'incremental');
      
      if (result) {
        // Save to Supabase
        await saveOrUpdateBackupInSupabase(websiteId, result);
        
        // Keep local database for compatibility
        const allBackups = getBackups();
        const newBackup: BackupRecord = {
          ...result,
          websiteId: websiteId,
          backupType: 'incremental' as const
        };
        saveBackups([...allBackups, newBackup]);
      }
      
      return result;
    } else {
      // Use traditional WordPress API backup method
      console.log(`Using traditional WordPress API for website ${websiteId}`);
      
      const credentials = await getWebsiteCredentials(websiteId);
      if (!credentials) {
        throw new Error("No WordPress credentials found for this website");
      }
      
      const result = await startWordPressBackup(websiteId, credentials);
      
      if (result) {
        // Save to Supabase
        await saveOrUpdateBackupInSupabase(websiteId, result);
        
        // Keep local database for compatibility
        const allBackups = getBackups();
        const newBackup: BackupRecord = {
          ...result,
          websiteId: websiteId,
          backupType: 'incremental' as const
        };
        saveBackups([...allBackups, newBackup]);
      }
      
      return result;
    }
  } catch (error) {
    console.error("Error creating backup:", error);
    toast({
      title: "Backup failed",
      description: "There was an error creating your backup. Please try again.",
      variant: "destructive",
    });
    return null;
  }
};

/**
 * Creates a full backup for a website (override for manual full backups)
 */
export const createFullBackup = async (websiteId: string): Promise<BackupResult | null> => {
  try {
    console.log(`Creating full backup for website ${websiteId}`);
    
    // Check if this website is connected via plugin
    const pluginRegistration = getPluginRegistration(websiteId);
    
    if (pluginRegistration) {
      // Use plugin-based backup with full type
      console.log(`Using plugin-based full backup for website ${websiteId}`);
      
      const result = await backupWebsiteViaPlugin(websiteId, 'full');
      
      if (result) {
        // Save to Supabase
        await saveOrUpdateBackupInSupabase(websiteId, result);
        
        // Keep local database for compatibility
        const allBackups = getBackups();
        const newBackup: BackupRecord = {
          ...result,
          websiteId: websiteId,
          backupType: 'full' as const
        };
        saveBackups([...allBackups, newBackup]);
      }
      
      return result;
    } else {
      // Use traditional WordPress API
      const credentials = await getWebsiteCredentials(websiteId);
      if (!credentials) {
        throw new Error("No WordPress credentials found for this website");
      }
      
      const result = await startWordPressBackup(websiteId, credentials);
      
      if (result) {
        // Save to Supabase
        await saveOrUpdateBackupInSupabase(websiteId, result);
        
        // Keep local database for compatibility
        const allBackups = getBackups();
        const newBackup: BackupRecord = {
          ...result,
          websiteId: websiteId,
          backupType: 'full' as const
        };
        saveBackups([...allBackups, newBackup]);
      }
      
      return result;
    }
  } catch (error) {
    console.error("Error creating full backup:", error);
    toast({
      title: "Full backup failed",
      description: "There was an error creating your full backup. Please try again.",
      variant: "destructive",
    });
    return null;
  }
};

/**
 * Deletes a backup for a website
 */
export const deleteBackup = async (websiteId: string, backupId: string): Promise<boolean> => {
  try {
    console.log(`Deleting backup ${backupId} for website ${websiteId}`);
    
    // Use Supabase for deletion
    const success = await deleteBackupFromSupabase(websiteId, backupId);
    
    if (success) {
      // Also remove from local storage for compatibility
      const allBackups = getBackups();
      const updatedBackups = allBackups.filter(backup => 
        !(backup.id === backupId && backup.websiteId === websiteId)
      );
      saveBackups(updatedBackups);
      
      toast({
        title: "Backup deleted",
        description: "The backup has been successfully deleted.",
      });
    }
    
    return success;
  } catch (error) {
    console.error(`Error deleting backup ${backupId} for website ${websiteId}:`, error);
    return false;
  }
};

/**
 * Calls the WordPress plugin API to start a backup - REAL IMPLEMENTATION
 */
const callWordPressPluginAPI = async (
  websiteId: string, 
  pluginRegistration: any, 
  backupType: 'full' | 'incremental',
  backupId: string
): Promise<BackupResult | null> => {
  try {
    const apiUrl = `${pluginRegistration.siteUrl}/wp-json/websitebackup/v1/backup/generate`;
    
    console.log(`Calling WordPress plugin API: ${apiUrl}`);
    console.log(`Backup type: ${backupType}, ID: ${backupId}`);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${pluginRegistration.apiKey}`
      },
      body: JSON.stringify({
        backup_id: backupId,
        backup_type: backupType,
        website_id: websiteId
      })
    });
    
    if (!response.ok) {
      throw new Error(`WordPress API call failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('WordPress plugin API response:', data);
    
    if (data.status === 'success') {
      return {
        id: data.backup_id || backupId,
        createdAt: new Date().toISOString(),
        status: 'in-progress',
        size: 'In progress...',
        progress: 0,
        backupType: backupType
      };
    } else {
      throw new Error(data.message || 'Backup initiation failed');
    }
  } catch (error) {
    console.error('Error calling WordPress plugin API:', error);
    throw error;
  }
};

/**
 * Monitors plugin backup progress by polling the WordPress API - REAL IMPLEMENTATION
 */
const monitorPluginBackupProgress = async (
  websiteId: string,
  backupId: string,
  pluginRegistration: any
): Promise<void> => {
  const checkProgress = async (): Promise<void> => {
    try {
      const statusUrl = `${pluginRegistration.siteUrl}/wp-json/websitebackup/v1/backup/${backupId}`;
      
      const response = await fetch(statusUrl, {
        headers: {
          'Authorization': `Bearer ${pluginRegistration.apiKey}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`Backup ${backupId} status:`, data);
        
        // Update backup in both local database and Supabase
        const allBackups = getBackups();
        const backupIndex = allBackups.findIndex(b => b.id === backupId && b.websiteId === websiteId);
        
        if (backupIndex !== -1) {
          const updatedBackup = {
            ...allBackups[backupIndex],
            status: data.status,
            size: data.size || allBackups[backupIndex].size,
            progress: data.status === 'completed' ? 100 : (allBackups[backupIndex].progress || 0) + 10,
            downloadUrl: data.download_url
          };
          
          allBackups[backupIndex] = updatedBackup;
          saveBackups(allBackups);
          
          // Update in Supabase as well
          await saveOrUpdateBackupInSupabase(websiteId, updatedBackup);
          
          if (data.status === 'completed') {
            toast({
              title: "Backup completed",
              description: "Your backup has been successfully completed and saved to the server.",
            });
            
            // Initiate security scan
            setTimeout(async () => {
              console.log(`Initiating security scan for completed backup ${backupId}`);
              await scanBackupForThreats(backupId, websiteId);
            }, 1000);
            
            return; // Stop monitoring
          } else if (data.status === 'failed') {
            toast({
              title: "Backup failed",
              description: "Your backup failed. Please check the WordPress plugin logs.",
              variant: "destructive",
            });
            return; // Stop monitoring
          }
        }
      }
      
      // Continue monitoring if backup is still in progress
      setTimeout(checkProgress, 5000); // Check every 5 seconds
      
    } catch (error) {
      console.error('Error checking backup progress:', error);
      // Continue monitoring even if there's an error
      setTimeout(checkProgress, 10000); // Check again in 10 seconds
    }
  };
  
  // Start monitoring after a short delay
  setTimeout(checkProgress, 2000);
};

/**
 * Gets detailed information about a backup
 */
export const getBackupDetails = async (websiteId: string, backupId: string): Promise<BackupResult | null> => {
  try {
    // Try Laravel backend first
    // const backupFromLaravel = await laravelBackupService.getBackupStatus(websiteId, backupId);
    // if (backupFromLaravel) {
    //   return backupFromLaravel;
    // }
    
    // Fallback to local database
    const allBackups = getBackups();
    const existingBackup = allBackups.find(b => b.id === backupId && b.websiteId === websiteId);
    
    if (existingBackup) {
      return {
        id: existingBackup.id,
        createdAt: existingBackup.createdAt,
        size: existingBackup.size,
        status: existingBackup.status,
        downloadUrl: existingBackup.downloadUrl || "",
        progress: existingBackup.progress || 0,
        backupType: existingBackup.backupType || 'full'
      };
    }
    
    return null;
  } catch (error) {
    console.error(`Error getting backup details for ${backupId}:`, error);
    return null;
  }
};

/**
 * Updates the progress of an in-progress backup
 */
export const updateBackupProgress = async (websiteId: string, backupId: string, progress: number): Promise<boolean> => {
  try {
    const allBackups = getBackups();
    const backupIndex = allBackups.findIndex(b => b.id === backupId && b.websiteId === websiteId);
    
    if (backupIndex === -1) {
      console.warn(`No backup found with ID ${backupId} for website ${websiteId}`);
      return false;
    }
    
    // Update the progress
    allBackups[backupIndex].progress = progress;
    
    // Update size based on progress
    if (progress < 100) {
      allBackups[backupIndex].size = `Calculating... (${progress}%)`;
    } else {
      allBackups[backupIndex].size = "45.2 MB";
      allBackups[backupIndex].status = "completed";
      
      // Updated to use the application URL
      allBackups[backupIndex].downloadUrl = `https://backup-site-hero.lovable.app/backups/${backupId}.zip`;
      
      // When backup is completed, initiate security scan
      setTimeout(async () => {
        console.log(`Initiating security scan for completed backup ${backupId}`);
        await scanBackupForThreats(backupId, websiteId);
      }, 1000);
    }
    
    const success = saveBackups(allBackups);
    return success;
  } catch (error) {
    console.error(`Error updating backup progress for ${backupId}:`, error);
    return false;
  }
};

/**
 * Disables automatic backups for a website
 */
export const disableAutomaticBackups = async (websiteId: string): Promise<boolean> => {
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 800));
    
    console.log(`Disabling automatic backups for website ${websiteId}`);
    // This would typically update a setting in the database
    
    toast({
      title: "Automatic backups disabled",
      description: "Automatic backups have been disabled for this website.",
    });
    
    return true;
  } catch (error) {
    console.error(`Error disabling automatic backups for website ${websiteId}:`, error);
    
    toast({
      title: "Operation failed",
      description: "Failed to disable automatic backups. Please try again.",
      variant: "destructive",
    });
    
    return false;
  }
};

/**
 * Simulates backup progress for demo purposes
 */
export const simulateBackupProgress = async (websiteId: string, backupId: string): Promise<void> => {
  let progress = 0;
  const interval = setInterval(async () => {
    progress += Math.floor(Math.random() * 10) + 1;
    
    if (progress > 100) progress = 100;
    
    await updateBackupProgress(websiteId, backupId, progress);
    
    if (progress >= 100) {
      clearInterval(interval);
      
      toast({
        title: "Backup completed",
        description: "Your backup has been successfully completed and is being scanned for security threats.",
      });
    }
  }, 3000); // Update every 3 seconds
};
